import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import {
  DeleteOutlined,
  FactoryOutlined,
  Inventory2Outlined,
  RoomOutlined,
  SaveOutlined,
} from '@mui/icons-material';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import { LocationMapModal } from 'views/DispatchImporters/LocationMapModal';
import { type LocationRecord } from 'views/DispatchImporters/LocationImporterView';
import MaterialCategoryAPI from 'api/material-category.api';
import GradeCategoryAPI from 'api/grade-category.api';
import LocationImporterAPI from 'api/location-importer.api';
// import useDispatchMode from 'hooks/useDispatchMode'; // Auto/Manual toggle hidden — see below
import { toMaterialCategoryList } from 'utils/helpers/materialCategory';
import { CategorySearchSelect } from 'views/DispatchImporters/CategoryManagement/CategorySearchSelect';
import { CategoryMultiSelect } from 'views/DispatchImporters/CategoryManagement/CategoryMultiSelect';
import { AddMaterialCategoryModal } from 'views/DispatchImporters/CategoryManagement/AddMaterialCategoryModal';
import { AddGradeCategoryModal } from 'views/DispatchImporters/CategoryManagement/AddGradeCategoryModal';
import {
  type MaterialCategoryRecord,
  type GradeCategoryRecord,
} from 'views/DispatchImporters/CategoryManagement/types';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SiteLocation {
  id: string;
  name: string;
  locationType: string;
  minutes: number | null;
  trucksCapacity: number | null;
  easting: number;
  northing: number;
  elevation: number;
  active: boolean;
  meta: Record<string, unknown>;
}

interface DirtyEntry {
  active?: boolean;
  minutes?: number | null;
  trucksCapacity?: number | null;
  meta?: Record<string, unknown>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const isCrusher = (type: string) =>
  ['crusher', 'crushing'].includes(type.toLowerCase());
const isStockpile = (type: string) => type.toLowerCase() === 'stockpile';
const isDumping = (type: string) => type.toLowerCase() === 'dumping';
const hasCoords = (loc: SiteLocation) =>
  loc.easting !== 0 && loc.northing !== 0;

const trimMetaStrings = (
  meta: Record<string, unknown>
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(meta).map(([k, v]) => [
      k,
      typeof v === 'string' ? v.trim() : v,
    ])
  );

const toLocationRecord = (loc: SiteLocation): LocationRecord => ({
  id: loc.id,
  name: loc.name,
  locationType: loc.locationType,
  easting: loc.easting,
  northing: loc.northing,
  elevation: loc.elevation,
  active: loc.active,
});

// ─── Sub-components ──────────────────────────────────────────────────────────

const SectionHeader = ({
  icon,
  title,
  description,
  activeCount,
  total,
  iconBg,
  iconColor,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  activeCount: number;
  total: number;
  iconBg: string | ((theme: Theme) => string);
  iconColor: string;
}) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: iconBg,
            color: iconColor,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="overline"
          fontWeight={700}
          sx={{ letterSpacing: '0.08em', lineHeight: 1 }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            ml: 'auto',
            px: 1.5,
            py: 0.3,
            borderRadius: '20px',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {t('site_config_v2_active_count', { active: activeCount, total })}
          </Typography>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ ml: 6 }}>
        {description}
      </Typography>
    </Box>
  );
};

const StyledTh = ({ children }: { children: React.ReactNode }) => (
  <TableCell
    sx={{
      fontWeight: 700,
      fontSize: '0.72rem',
      textTransform: 'uppercase',
      letterSpacing: '0.07em',
      color: 'text.secondary',
      whiteSpace: 'nowrap',
      py: 1.2,
      borderBottom: '1px solid',
      borderColor: 'divider',
    }}
  >
    {children}
  </TableCell>
);

const NumericCell = ({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (v: string) => void;
}) => (
  <TextField
    size="small"
    type="number"
    value={value ?? ''}
    onChange={(e) => {
      onChange(e.target.value);
    }}
    inputProps={{ min: 0, style: { textAlign: 'center', width: 64 } }}
    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
  />
);

// ─── Main Component ───────────────────────────────────────────────────────────

export const SiteConfigV2View = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { customer } = useAppSelector((s) => s.authReducer);
  const materialCategoryApi = useRef(new MaterialCategoryAPI()).current;
  const gradeCategoryApi = useRef(new GradeCategoryAPI()).current;
  const locationApi = useRef(new LocationImporterAPI()).current;
  // Auto/Manual dispatch mode toggle is hidden (mode fixed to 'manual') —
  // commented with the toggle JSX below instead of removed
  // const { mode, setMode, loading: modeLoading } = useDispatchMode();
  // const [modeSaving, setModeSaving] = useState(false);

  const [locations, setLocations] = useState<SiteLocation[]>([]);
  const [dirty, setDirty] = useState<Map<string, DirtyEntry>>(new Map());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mapLocation, setMapLocation] = useState<LocationRecord | null>(null);
  const [materialCategories, setMaterialCategories] = useState<
    MaterialCategoryRecord[]
  >([]);
  const [gradeCategories, setGradeCategories] = useState<GradeCategoryRecord[]>(
    []
  );
  const [addMaterialCategoryTarget, setAddMaterialCategoryTarget] = useState<{
    locationId: string;
    prefill: string;
    current: string[];
  } | null>(null);
  const [addGradeCategoryTarget, setAddGradeCategoryTarget] = useState<{
    locationId: string;
    prefill: string;
  } | null>(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await locationApi.getLocations();
      setLocations(
        data.map((r) => ({
          id: r.locationId,
          name: r.name,
          locationType: r.locationType,
          minutes: r.minutes,
          trucksCapacity: r.trucksCapacity,
          easting: r.easting ?? 0,
          northing: r.northing ?? 0,
          elevation: r.elevation ?? 0,
          active: r.active,
          meta: r.meta ?? {},
        }))
      );
    } catch (e) {
      dispatch(
        showNotification({
          message: t('site_config_v2_fetch_failed'),
          type: 'error',
        })
      );
    } finally {
      setLoading(false);
    }
  }, [locationApi]);

  useEffect(() => {
    void fetchLocations();
  }, [fetchLocations]);

  // ── Material categories ────────────────────────────────────────────────────

  const fetchMaterialCategories = useCallback(async () => {
    try {
      const records = await materialCategoryApi.getAll();
      setMaterialCategories(records);
    } catch (_) {}
  }, [materialCategoryApi]);

  const fetchGradeCategories = useCallback(async () => {
    try {
      const records = await gradeCategoryApi.getAll();
      setGradeCategories(records);
    } catch (_) {}
  }, [gradeCategoryApi]);

  useEffect(() => {
    void fetchMaterialCategories();
    void fetchGradeCategories();
  }, [fetchMaterialCategories, fetchGradeCategories]);

  // ── Dirty tracking ─────────────────────────────────────────────────────────

  const markDirty = useCallback((id: string, patch: Partial<DirtyEntry>) => {
    setDirty((prev) => {
      const next = new Map(prev);
      const existing = next.get(id) ?? {};
      next.set(id, {
        ...existing,
        ...patch,
        meta:
          patch.meta != null
            ? { ...(existing.meta ?? {}), ...patch.meta }
            : existing.meta,
      });
      return next;
    });
  }, []);

  const merged = useMemo<SiteLocation[]>(
    () =>
      locations.map((loc) => {
        const d = dirty.get(loc.id);
        if (!d) return loc;
        return {
          ...loc,
          ...(d.active !== undefined && { active: d.active }),
          ...(d.minutes !== undefined && { minutes: d.minutes }),
          ...(d.trucksCapacity !== undefined && {
            trucksCapacity: d.trucksCapacity,
          }),
          meta: { ...loc.meta, ...(d.meta ?? {}) },
        };
      }),
    [locations, dirty]
  );

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (dirty.size === 0) return;
    setSaving(true);
    try {
      await Promise.all(
        Array.from(dirty.entries()).map(async ([id, patch]) => {
          const loc = locations.find((l) => l.id === id);
          const body: Record<string, unknown> = { ...patch };
          if (patch.meta != null && loc) {
            body.meta = trimMetaStrings({ ...loc.meta, ...patch.meta });
          }
          await locationApi.updateLocation(id, body);
        })
      );
      setDirty(new Map());
      await fetchLocations();
      dispatch(
        showNotification({
          message: t('site_config_v2_saved'),
          type: 'success',
        })
      );
    } catch (e) {
      dispatch(
        showNotification({
          message: t('site_config_v2_save_failed'),
          type: 'error',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Filtered sections ──────────────────────────────────────────────────────

  const byName = (a: SiteLocation, b: SiteLocation) =>
    a.name.localeCompare(b.name);
  const crushers = merged.filter((l) => isCrusher(l.locationType)).sort(byName);
  const stockpiles = merged
    .filter((l) => isStockpile(l.locationType))
    .sort(byName);
  const dumpingPoints = merged
    .filter((l) => isDumping(l.locationType))
    .sort(byName);
  const materialCategoryCodes = materialCategories.map((m) => m.enumValue);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <CustomMuiThemeProvider>
      <Box sx={{ p: 3, maxWidth: 1200 }}>
        {/* Page header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: 3,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: 'background.default',
            py: 2,
            mx: -3,
            px: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {t('site_config_v2_title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {t('site_config_v2_subtitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Auto/Manual dispatch mode toggle — hidden, mode is fixed to
                'manual' (see useDispatchMode.ts)
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t('manual_dispatch_mode_label')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: mode === 'auto' ? 'text.primary' : 'text.secondary',
                  fontWeight: mode === 'auto' ? 700 : 400,
                }}
              >
                {t('manual_dispatch_mode_auto')}
              </Typography>
              <Switch
                size="small"
                checked={mode === 'manual'}
                disabled={modeLoading || modeSaving}
                onChange={(e) => {
                  const next = e.target.checked ? 'manual' : 'auto';
                  setModeSaving(true);
                  setMode(next)
                    .catch(() => {
                      dispatch(
                        showNotification({
                          message: t('manual_dispatch_mode_save_failed'),
                          type: 'error',
                        })
                      );
                    })
                    .finally(() => {
                      setModeSaving(false);
                    });
                }}
                color="primary"
              />
              <Typography
                variant="body2"
                sx={{
                  color: mode === 'manual' ? 'text.primary' : 'text.secondary',
                  fontWeight: mode === 'manual' ? 700 : 400,
                }}
              >
                {t('manual_dispatch_mode_manual')}
              </Typography>
            </Box>
            */}
            <Button
              variant="contained"
              startIcon={
                saving ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SaveOutlined sx={{ fontSize: 16 }} />
                )
              }
              disabled={saving || dirty.size === 0}
              onClick={() => {
                void handleSave();
              }}
              sx={{ borderRadius: 2, fontWeight: 600, px: 2.5 }}
            >
              {saving ? t('site_config_v2_saving') : t('site_config_v2_save')}
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* ── Crushers ──────────────────────────────────────────────── */}
            <SectionCard>
              <SectionHeader
                icon={<FactoryOutlined sx={{ fontSize: 18 }} />}
                title={t('site_config_v2_crushers')}
                description={t('site_config_v2_crushers_desc')}
                activeCount={crushers.filter((c) => c.active).length}
                total={crushers.length}
                iconBg={(theme) => alpha(theme.palette.primary.main, 0.12)}
                iconColor="primary.main"
              />
              <SiteTable>
                <TableHead>
                  <TableRow>
                    <StyledTh>{t('site_config_v2_col_name')}</StyledTh>
                    <StyledTh>
                      {t('site_config_v2_col_crusher_capacity')}
                    </StyledTh>
                    <StyledTh>{t('site_config_v2_col_dumping_time')}</StyledTh>
                    <StyledTh>{t('site_config_v2_col_target_blend')}</StyledTh>
                    <StyledTh>
                      {t('site_config_v2_col_material_category')}
                    </StyledTh>
                    <StyledTh>
                      {t('site_config_v2_col_queue_capacity')}
                    </StyledTh>
                    <StyledTh>{t('site_config_v2_col_active')}</StyledTh>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {crushers.length === 0 && (
                    <EmptyRow colSpan={7} label={t('site_config_v2_no_data')} />
                  )}
                  {crushers.map((loc) => {
                    const targetBlend =
                      loc.meta?.targetBlend != null
                        ? (loc.meta.targetBlend as number)
                        : null;
                    const crusherCapacity =
                      loc.meta?.crusherCapacity != null
                        ? (loc.meta.crusherCapacity as number)
                        : null;
                    const categories = toMaterialCategoryList(
                      loc.meta?.materialCategory
                    );
                    return (
                      <TableRow
                        key={loc.id}
                        sx={{ opacity: loc.active ? 1 : 0.45 }}
                      >
                        <NameCell
                          name={loc.name}
                          showMap={hasCoords(loc)}
                          onMapClick={() => {
                            setMapLocation(toLocationRecord(loc));
                          }}
                        />
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <NumericCell
                              value={crusherCapacity}
                              onChange={(v) => {
                                markDirty(loc.id, {
                                  meta: {
                                    crusherCapacity: v ? parseFloat(v) : null,
                                  },
                                });
                              }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              t/hr
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <NumericCell
                            value={loc.minutes}
                            onChange={(v) => {
                              markDirty(loc.id, {
                                minutes: v ? parseInt(v, 10) : null,
                              });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <NumericCell
                            value={targetBlend}
                            onChange={(v) => {
                              markDirty(loc.id, {
                                meta: { targetBlend: v ? parseFloat(v) : null },
                              });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ minWidth: 180 }}>
                            <CategoryMultiSelect
                              label=""
                              codes={materialCategoryCodes}
                              getLabel={(code) => code}
                              value={categories}
                              onChange={(codes) => {
                                markDirty(loc.id, {
                                  meta: { materialCategory: codes },
                                });
                              }}
                              addNewLabel={t(
                                'site_config_v2_add_new_material_category'
                              )}
                              onAddNew={(searchText) => {
                                setAddMaterialCategoryTarget({
                                  locationId: loc.id,
                                  prefill: searchText,
                                  current: categories,
                                });
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <NumericCell
                            value={loc.trucksCapacity}
                            onChange={(v) => {
                              markDirty(loc.id, {
                                trucksCapacity: v ? parseInt(v, 10) : null,
                              });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <ActiveToggle
                            checked={loc.active}
                            onChange={(v) => {
                              markDirty(loc.id, { active: v });
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </SiteTable>
            </SectionCard>

            {/* ── Stockpiles ────────────────────────────────────────────── */}
            <SectionCard>
              <SectionHeader
                icon={<Inventory2Outlined sx={{ fontSize: 18 }} />}
                title={t('site_config_v2_stockpiles')}
                description={t('site_config_v2_stockpiles_desc')}
                activeCount={stockpiles.filter((s) => s.active).length}
                total={stockpiles.length}
                iconBg={(theme) => alpha(theme.palette.warning.main, 0.12)}
                iconColor="warning.main"
              />
              <Box
                sx={{
                  display: 'flex',
                  gap: 2.5,
                  flexWrap: 'wrap',
                  mb: 2,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                }}
              >
                {(
                  [
                    { key: 'HG', colorKey: 'success' as const },
                    { key: 'MG', colorKey: 'warning' as const },
                    { key: 'LG', colorKey: 'default' as const },
                  ] as Array<{
                    key: string;
                    colorKey: 'success' | 'warning' | 'default';
                  }>
                ).map(({ key, colorKey }) => (
                  <Box
                    key={key}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <Box
                      sx={{
                        px: 1,
                        py: 0.3,
                        borderRadius: 1,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        bgcolor:
                          colorKey === 'success'
                            ? (theme) => alpha(theme.palette.success.main, 0.15)
                            : colorKey === 'warning'
                            ? (theme) => alpha(theme.palette.warning.main, 0.15)
                            : (theme) =>
                                alpha(theme.palette.text.secondary, 0.12),
                        color:
                          colorKey === 'success'
                            ? 'success.main'
                            : colorKey === 'warning'
                            ? 'warning.main'
                            : 'text.secondary',
                      }}
                    >
                      {key}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      <Box
                        component="span"
                        fontWeight={600}
                        color="text.primary"
                      >
                        {t(`site_config_v2_grade_${key.toLowerCase()}`)}
                      </Box>
                      {' · '}
                      {t(`site_config_v2_grade_${key.toLowerCase()}_range`)}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <SiteTable>
                <TableHead>
                  <TableRow>
                    <StyledTh>{t('site_config_v2_col_name')}</StyledTh>
                    <StyledTh>{t('site_config_v2_col_dumping_time')}</StyledTh>
                    <StyledTh>
                      {t('site_config_v2_col_grade_category')}
                    </StyledTh>
                    <StyledTh>
                      {t('site_config_v2_col_queue_capacity')}
                    </StyledTh>
                    <StyledTh>{t('site_config_v2_col_active')}</StyledTh>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockpiles.length === 0 && (
                    <EmptyRow colSpan={5} label={t('site_config_v2_no_data')} />
                  )}
                  {stockpiles.map((loc) => {
                    const grade =
                      (loc.meta?.MaterialGradeQuality as string) ?? '';
                    return (
                      <TableRow
                        key={loc.id}
                        sx={{ opacity: loc.active ? 1 : 0.45 }}
                      >
                        <NameCell
                          name={loc.name}
                          showMap={hasCoords(loc)}
                          onMapClick={() => {
                            setMapLocation(toLocationRecord(loc));
                          }}
                        />
                        <TableCell>
                          <NumericCell
                            value={loc.minutes}
                            onChange={(v) => {
                              markDirty(loc.id, {
                                minutes: v ? parseInt(v, 10) : null,
                              });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ minWidth: 180 }}>
                            <CategorySearchSelect
                              label=""
                              codes={gradeCategories.map(
                                (g) => g.gradeCategory
                              )}
                              getLabel={(code) => {
                                const rec = gradeCategories.find(
                                  (g) => g.gradeCategory === code
                                );
                                return rec
                                  ? `${code} (${rec.minGrade}–${rec.maxGrade})`
                                  : code;
                              }}
                              value={grade || null}
                              onChange={(code) => {
                                markDirty(loc.id, {
                                  meta: {
                                    MaterialGradeQuality: code ?? '',
                                  },
                                });
                              }}
                              addNewLabel={t(
                                'site_config_v2_add_new_grade_category'
                              )}
                              onAddNew={(searchText) => {
                                setAddGradeCategoryTarget({
                                  locationId: loc.id,
                                  prefill: searchText,
                                });
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <NumericCell
                            value={loc.trucksCapacity}
                            onChange={(v) => {
                              markDirty(loc.id, {
                                trucksCapacity: v ? parseInt(v, 10) : null,
                              });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <ActiveToggle
                            checked={loc.active}
                            onChange={(v) => {
                              markDirty(loc.id, { active: v });
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </SiteTable>
            </SectionCard>

            {/* ── Dumping Points ────────────────────────────────────────── */}
            <SectionCard>
              <SectionHeader
                icon={<DeleteOutlined sx={{ fontSize: 18 }} />}
                title={t('site_config_v2_dumps')}
                description={t('site_config_v2_dumps_desc')}
                activeCount={dumpingPoints.filter((d) => d.active).length}
                total={dumpingPoints.length}
                iconBg={(theme) => alpha(theme.palette.text.secondary, 0.1)}
                iconColor="text.secondary"
              />
              <SiteTable>
                <TableHead>
                  <TableRow>
                    <StyledTh>{t('site_config_v2_col_name')}</StyledTh>
                    <StyledTh>{t('site_config_v2_col_dumping_time')}</StyledTh>
                    <StyledTh>
                      {t('site_config_v2_col_material_category')}
                    </StyledTh>
                    <StyledTh>
                      {t('site_config_v2_col_queue_capacity')}
                    </StyledTh>
                    <StyledTh>{t('site_config_v2_col_active')}</StyledTh>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dumpingPoints.length === 0 && (
                    <EmptyRow colSpan={5} label={t('site_config_v2_no_data')} />
                  )}
                  {dumpingPoints.map((loc) => {
                    const categories = toMaterialCategoryList(
                      loc.meta?.materialCategory
                    );
                    return (
                      <TableRow
                        key={loc.id}
                        sx={{ opacity: loc.active ? 1 : 0.45 }}
                      >
                        <NameCell
                          name={loc.name}
                          showMap={hasCoords(loc)}
                          onMapClick={() => {
                            setMapLocation(toLocationRecord(loc));
                          }}
                        />
                        <TableCell>
                          <NumericCell
                            value={loc.minutes}
                            onChange={(v) => {
                              markDirty(loc.id, {
                                minutes: v ? parseInt(v, 10) : null,
                              });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ minWidth: 180 }}>
                            <CategoryMultiSelect
                              label=""
                              codes={materialCategoryCodes}
                              getLabel={(code) => code}
                              value={categories}
                              onChange={(codes) => {
                                markDirty(loc.id, {
                                  meta: { materialCategory: codes },
                                });
                              }}
                              addNewLabel={t(
                                'site_config_v2_add_new_material_category'
                              )}
                              onAddNew={(searchText) => {
                                setAddMaterialCategoryTarget({
                                  locationId: loc.id,
                                  prefill: searchText,
                                  current: categories,
                                });
                              }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <NumericCell
                            value={loc.trucksCapacity}
                            onChange={(v) => {
                              markDirty(loc.id, {
                                trucksCapacity: v ? parseInt(v, 10) : null,
                              });
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <ActiveToggle
                            checked={loc.active}
                            onChange={(v) => {
                              markDirty(loc.id, { active: v });
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </SiteTable>
            </SectionCard>
          </>
        )}
      </Box>

      <LocationMapModal
        open={mapLocation !== null}
        location={mapLocation}
        onClose={() => {
          setMapLocation(null);
        }}
      />

      <AddMaterialCategoryModal
        open={addMaterialCategoryTarget !== null}
        initialCode={addMaterialCategoryTarget?.prefill}
        onClose={() => {
          setAddMaterialCategoryTarget(null);
        }}
        onCreated={(created) => {
          if (addMaterialCategoryTarget) {
            markDirty(addMaterialCategoryTarget.locationId, {
              meta: {
                materialCategory: [
                  ...addMaterialCategoryTarget.current,
                  created.enumValue,
                ],
              },
            });
          }
          setAddMaterialCategoryTarget(null);
          void fetchMaterialCategories();
        }}
      />
      <AddGradeCategoryModal
        open={addGradeCategoryTarget !== null}
        initialCode={addGradeCategoryTarget?.prefill}
        onClose={() => {
          setAddGradeCategoryTarget(null);
        }}
        onCreated={(created) => {
          if (addGradeCategoryTarget) {
            markDirty(addGradeCategoryTarget.locationId, {
              meta: { MaterialGradeQuality: created.gradeCategory },
            });
          }
          setAddGradeCategoryTarget(null);
          void fetchGradeCategories();
        }}
      />
    </CustomMuiThemeProvider>
  );
};

// ─── Small local helpers ──────────────────────────────────────────────────────

const SectionCard = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 3,
      p: 2.5,
      mb: 2,
    }}
  >
    {children}
  </Box>
);

const SiteTable = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ overflowX: 'auto' }}>
    <Table size="small" sx={{ minWidth: 520 }}>
      {children}
    </Table>
  </Box>
);

const EmptyRow = ({ colSpan, label }: { colSpan: number; label: string }) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
      <Typography variant="body2" color="text.disabled">
        {label}
      </Typography>
    </TableCell>
  </TableRow>
);

const NameCell = ({
  name,
  showMap,
  onMapClick,
}: {
  name: string;
  showMap: boolean;
  onMapClick: () => void;
}) => (
  <TableCell>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="body2" fontWeight={600}>
        {name}
      </Typography>
      {showMap && (
        <Tooltip title="View on map" placement="top">
          <IconButton
            size="small"
            onClick={onMapClick}
            sx={{ color: 'primary.main', p: 0.3 }}
          >
            <RoomOutlined sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  </TableCell>
);

const ActiveToggle = ({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) => {
  const { t } = useTranslation();
  return (
    <Tooltip
      title={
        checked ? t('site_config_v2_active') : t('site_config_v2_inactive')
      }
      placement="top"
    >
      <Switch
        size="small"
        checked={checked}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
        color="success"
      />
    </Tooltip>
  );
};
